import { defineComponent as _defineComponent } from 'vue'
import Vue, {
  computed,
  onMounted,
  ref,
  watch,
} from "vue";

import {
  Issuer,
} from "@/model/Issuer";

import {
  ErrorDialog,
} from "@/components/dialogs";

import IconTextButton from "@/components/buttons/IconTextButton.vue";

import TimeSpanDialog from "@/components/base/TimeSpanDialog.vue";

import DeadlineDialog from "@/components/base/DeadlineDialog.vue";

import {
  ReportDownloadModel,
} from "@/model/ReportDownloadModel";

import {
  ReportRequester,
} from "@/scripts/ProfiControlRequests/ReportRequester";

import {
  useI18n,
} from "@/composables/useI18n";

import moment from "moment/moment";

import {
  DateFormats,
} from "@/scripts/Common/DateFormats";

import {
  fetchIssuerCards,
  fetchIssuers,
} from "@/components/issuers/scripts/issuers/requests";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  downloadIssuerReportsPolicy,
} from "@/authorization/policies";

import {
  getToken,
} from "@/scripts/Common";

import {
  ISSUER_ID_STORAGE_KEY,
  } from "@/components/issuers/scripts";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import { debounce } from "@/scripts/Common/HelperFunctions";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS
} from  "@/components/issuers/scripts/constants";

import {
  loadIssuerFromStorageOrFirst
} from "./scripts/issuers/helper";
import InfoSnackbar from "../children/InfoSnackbar.vue";
import {useSnackbar} from "@/composables";


export default /*#__PURE__*/_defineComponent({
  __name: 'IssuerReports',
  emits: [
  'onFetchError'
],
  setup(__props, { emit: emits }) {


const {
  locale,
  t,
} = useI18n();

const snackbarRef = ref<InstanceType<typeof InfoSnackbar> | null>(null);
const chargeReportDialogRef = ref<InstanceType<typeof TimeSpanDialog> | null>(null);
const chargeLimitReportDialogRef = ref<InstanceType<typeof TimeSpanDialog> | null>(null);
const cardBalanceReportDialogRef = ref<InstanceType<typeof DeadlineDialog> | null>(null);

const {
  snackbarStatus,
  snackbarText,
  showSnackbar,
} = useSnackbar(snackbarRef);

const gatewayUrl = Vue.prototype.$config.GatewayUrl;
const issuers = ref<Issuer[]>([]);
const comboSelection = ref<Issuer | undefined>(undefined);
const issuerSearchInput = ref<string | undefined>(undefined);
const hasCards = ref<boolean>(false);



let ignoreIssuerSearchInputChange = false; // when the issuer is selected from the combobox, the issuerSearchInput change that follows should not trigger a reload of the issuer list

const onHandleFetchError = ({error}: { error: any }) => {
  emits('onFetchError', {error})
};

const hasIssuerCards = async () => {
  if(!comboSelection.value || !comboSelection.value.Id) return false;

  const {
    response,
    error,
  } = await fetchIssuerCards(comboSelection.value.Id);

  if (error) {
    onHandleFetchError({error});
    return false;
  }

  return (response ?? []).length > 0;
};

const isMissingPaycards = async () => !await hasIssuerCards();

const showMissingPaycardsErrorSnackbar = () => {
  showSnackbar({
    status: "error",
    statusText: [t("no_paycards")]
  });
};

const onHandleShowCardBalanceReportDialog = async () => {
  const isMissingCards = await isMissingPaycards();
  if(isMissingCards){
    showMissingPaycardsErrorSnackbar();
    return;
  }

  cardBalanceReportDialogRef.value?.show();
};

const onHandleDownloadCardBalanceReport = (deadline: string) => {
  const url = `${gatewayUrl}/api/v1/admin/report/issuer/${comboSelection.value?.Id}/card-report/${deadline}`;
  const reportName = t("reports.card_balance_file_name").toString();
  const fileModel = new ReportDownloadModel(reportName, deadline);
  ReportRequester.DownloadReport(url, getToken(), fileModel, locale.value)
};

const onHandleShowChargeLimitReportDialog = () => {
  chargeLimitReportDialogRef.value?.show();
};

const onHandleDownloadChargeLimitReport = (startDate: string, endDate: string) => {
  const url = `${gatewayUrl}/api/v1/admin/report/issuer/chargelimit-report?issuerId=${comboSelection.value?.Id}&startDate=${startDate}&endDate=${endDate}`;
  const reportName = t("reports.charge_limit_file_name").toString();
  const fileModel = new ReportDownloadModel(reportName, startDate, endDate);
  ReportRequester.DownloadReport(url, getToken(), fileModel, locale.value);
};

const onHandleShowChargeReportDialog = () => {
  chargeReportDialogRef.value?.show();
};

const onHandleDownloadChargeReport = (startDate: string, endDate: string) => {
  const url = `${gatewayUrl}/api/v1/admin/report/issuer/webcharge-report?issuerId=${comboSelection.value?.Id}&startDate=${startDate}&endDate=${endDate}`;
  const reportName = t("reports.charge_file_name").toString();
  const fileModel = new ReportDownloadModel(reportName, startDate, endDate);
  ReportRequester.DownloadReport(url, getToken(), fileModel, locale.value);
};

const onClickDownloadBudgetReport = async () => {
  const isMissingCards = await isMissingPaycards();
  if(isMissingCards){
    showMissingPaycardsErrorSnackbar();
    return;
  }

  const url = `${gatewayUrl}/api/v1/admin/report/issuer/${comboSelection.value?.Id}/budget-report`;
  const reportName = t("reports.budget_file_name").toString();
  const fileModel = new ReportDownloadModel(reportName, moment().format(DateFormats.ISO8601_LONG));
  ReportRequester.DownloadReport(url, getToken(), fileModel, locale.value);
};

const initializeIssuer = async () => {
  const { response, error } = await loadIssuerFromStorageOrFirst();
  if (error) {
    onHandleFetchError({ error });
    return;
  }

  comboSelection.value = response;
}

const loadIssuers = async () => {

  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if(issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
}

const onHandleSelectionChange = () => {
  const issuerId = comboSelection.value?.Id;
  if(!issuerId || !(comboSelection.value instanceof Issuer)) return;
  ignoreIssuerSearchInputChange = true;
  saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY)
}

watch(() => issuerSearchInput.value, debounce(() => {
  if(ignoreIssuerSearchInputChange) {
    ignoreIssuerSearchInputChange = false;
    return;
  }
  loadIssuers();
}));

onMounted(async () => {
  await initializeIssuer();
});

return { __sfc: true,locale, t, snackbarRef, chargeReportDialogRef, chargeLimitReportDialogRef, cardBalanceReportDialogRef, snackbarStatus, snackbarText, showSnackbar, gatewayUrl, issuers, comboSelection, issuerSearchInput, hasCards, emits, ignoreIssuerSearchInputChange, onHandleFetchError, hasIssuerCards, isMissingPaycards, showMissingPaycardsErrorSnackbar, onHandleShowCardBalanceReportDialog, onHandleDownloadCardBalanceReport, onHandleShowChargeLimitReportDialog, onHandleDownloadChargeLimitReport, onHandleShowChargeReportDialog, onHandleDownloadChargeReport, onClickDownloadBudgetReport, initializeIssuer, loadIssuers, onHandleSelectionChange, IconTextButton, TimeSpanDialog, DeadlineDialog, hasAccessRights, downloadIssuerReportsPolicy, InfoSnackbar }
}

})